import { CalendarRange, ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn, formatDateStringForDisplay } from "../../../lib/utils";

export interface DateRange {
  from: string;
  to: string;
}

export interface DateRangeQuickSelect {
  name: string;
  dateRange: DateRange;
}

interface DateRangeSelectProps {
  name?: React.ReactNode;
  selectedRange: DateRange;
  defaultSelection: DateRange;
  quickSelectOptions?: DateRangeQuickSelect[];
  onChange: (dateRange: DateRange) => void;
  maxDate?: string;
  dropdownClassName?: string;
  rootDateRangeSelectClassName?: string;
}

export const DateRangeSelect: React.FC<DateRangeSelectProps> = ({
  name,
  selectedRange,
  defaultSelection,
  quickSelectOptions,
  onChange,
  maxDate,
  dropdownClassName,
  rootDateRangeSelectClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleIsOpen = () => {
    setIsOpen((current) => !current);
  };

  const [preApplySelection, setPreApplySelection] =
    useState<DateRange>(selectedRange);

  const onFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPreApplySelection((existingRange) => ({
      ...existingRange,
      from: e.target.value,
    }));
  };

  const onToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPreApplySelection((existingRange) => ({
      ...existingRange,
      to: e.target.value,
    }));
  };

  const resetToDefault = () => {
    setPreApplySelection(defaultSelection);
  };

  const normalizeRange = (range: DateRange): DateRange => {
    let from = range.from;
    let to = range.to;

    // Clamp to maxDate (lexicographic compare works for YYYY-MM-DD).
    if (maxDate) {
      if (from && from > maxDate) from = maxDate;
      if (to && to > maxDate) to = maxDate;
    }

    // Ensure to >= from.
    if (from && to && to < from) {
      to = from;
    }

    return { from, to };
  };

  const applySelection = (selection: DateRange) => {
    const normalized = normalizeRange(selection);
    onChange(normalized);
    setPreApplySelection(normalized);
    toggleIsOpen();
  };

  const fromMax = maxDate
    ? preApplySelection.to
      ? preApplySelection.to < maxDate
        ? preApplySelection.to
        : maxDate
      : maxDate
    : preApplySelection.to || undefined;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className={
          cn(isOpen
            ? "px-3 py-2 border border-blue-500 bg-blue-50 text-blue-600 rounded-md text-sm flex items-center"
            : "px-3 py-2 border border-gray-200 bg-white hover:bg-gray-50 rounded-md text-sm flex items-center", rootDateRangeSelectClassName)
        }
        onClick={() => {
          toggleIsOpen();
        }}
      >
        <CalendarRange className="h-3.5 w-3.5 mr-1.5" />
        {name && <span className="mr-1.5">{name}</span>}
        {`${formatDateStringForDisplay(selectedRange.from)} - ${formatDateStringForDisplay(selectedRange.to)}`}
        {isOpen ? (
          <ChevronUp className="ml-1 h-4 w-4" />
        ) : (
          <ChevronDown className="ml-1 h-4 w-4" />
        )}
      </button>
      {isOpen && (
        <div
          className={`absolute mt-1 z-10 w-72 bg-white border border-gray-200 rounded-md shadow-lg ${dropdownClassName ?? ""}`}
        >
          <div className="p-3">
            <div className="text-xs font-medium text-gray-500 mb-3">
              SELECT DATE RANGE
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-700 mb-1">From</label>
                <input
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md"
                  type="date"
                  value={preApplySelection.from}
                  onChange={onFromDateChange}
                  max={fromMax}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">To</label>
                <input
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md"
                  type="date"
                  onChange={onToDateChange}
                  value={preApplySelection.to}
                  min={preApplySelection.from || undefined}
                  max={maxDate}
                />
              </div>
              <div className="flex justify-between pt-2">
                <button
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                  onClick={resetToDefault}
                >
                  Reset
                </button>
                <button
                  className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                  onClick={() => {
                    applySelection(preApplySelection);
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
            {quickSelectOptions?.length && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="text-xs font-medium text-gray-500 mb-2">
                  QUICK SELECTIONS
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {quickSelectOptions.map((quickSelect) => (
                    <button
                      key={quickSelect.name}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 text-center"
                      onClick={() => {
                        applySelection(quickSelect.dateRange);
                      }}
                    >
                      {quickSelect.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
